import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't redirect on 401 errors for login requests or if we're already on the login page
    if (error.response?.status === 401 && 
        error.config.url !== "/auth/me" && 
        error.config.url !== "/auth/login" && 
        !window.location.pathname.includes('/auth/login')) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (credentials) => {
    const response = await api.post("/auth/login", credentials);
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
    }
    return response;
  },
  register: async (userData) => {
    const response = await api.post("/auth/register", userData);
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
    }
    return response;
  },
  logout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("token");
    }
  },
  getProfile: () => api.get("/auth/me"),
  getSessions: () => api.get("/auth/sessions"),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  revokeAllSessions: () => api.delete("/auth/sessions"),
};

const createUploadConfig = (onProgress) => ({
  onUploadProgress: (progressEvent) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    if (onProgress) {
      onProgress(percentCompleted);
    }
  },
  headers: {
    "Content-Type": "multipart/form-data",
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

export const videosAPI = {
  getAll: (params) => api.get("/videos", { params }),
  getById: (id) => api.get(`/videos/${id}`),
  create: (data, onProgress) => {
    console.log("Creating video with data:", data);
    return api.post("/videos", data, createUploadConfig(onProgress));
  },
  update: (id, data, onProgress) => {
    console.log("Updating video with data:", data);
    return api.put(`/videos/${id}`, data, createUploadConfig(onProgress));
  },
  delete: (id) => api.delete(`/videos/${id}`),
};

export const booksAPI = {
  getAll: (params) => api.get("/books", { params }),
  getCount: () => api.get("/books/count"),
  getById: (id) => api.get(`/books/${id}`),
  create: (data, onProgress) =>
    api.post("/books", data, createUploadConfig(onProgress)),
  update: (id, data, onProgress) =>
    api.put(`/books/${id}`, data, createUploadConfig(onProgress)),
  delete: (id) => api.delete(`/books/${id}`),
  getRatings: (id) => api.get(`/books/${id}/ratings`),
  rate: (id, rating) => api.post(`/books/${id}/ratings`, { rating }),
  getInProgressCount: () => api.get("/books/in-progress/count"),
  // New unified endpoint for all dashboard stats in a single request
  getDashboardStats: () => api.get("/books/dashboard/stats"),
  download: async (id, sectionStart = null, sectionEnd = null) => {
    const params = new URLSearchParams();
    const request_type = sectionStart && sectionEnd ? "section" : "full";
    params.append("request_type", request_type);
    if (sectionStart) params.append("section_start", sectionStart);
    if (sectionEnd) params.append("section_end", sectionEnd);

    const response = await api.get(`/books/${id}/download?${params}`, {
      responseType: "blob",
    });

    // Get filename from response headers or use default
    const contentDisposition = response.headers["content-disposition"];
    let filename = "book.pdf";
    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
        contentDisposition
      );
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }
    }

    // Create and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const accessRequestAPI = {
  create: (bookId, data) => api.post("/access-requests", { ...data, bookId }),
  update: (requestId, data) => api.put(`/access-requests/${requestId}`, data),
  getAll: (params) => api.get("/access-requests", { params }),
  getForBook: (bookId) => api.get("/access-requests", { params: { bookId } }),
  approve: (requestId, reviewNotes) =>
    api.patch(`/access-requests/${requestId}/status`, {
      status: "approved",
      reviewNotes,
    }),
  deny: (requestId, reviewNotes) =>
    api.patch(`/access-requests/${requestId}/status`, {
      status: "denied",
      reviewNotes,
    }),
  sendNotification: (requestId, options) =>
    api.post(`/access-requests/${requestId}/notification`, options),
  // Get all approved requests for current user with download instructions
  getApprovedRequests: () => api.get("/access-requests/approved"),
  // Update an access request after download
  markAsDownloaded: (requestId) =>
    api.patch(`/access-requests/${requestId}/downloaded`),
  // Get download history for all user's approved requests
  getDownloadHistory: () => api.get("/access-requests/downloads"),
};

export const categoriesAPI = {
  getAll: () => api.get("/categories"),
  getCount: () => api.get("/categories/count"),
  getPinned: () => api.get("/categories/pinned"),
  getPinnedCount: () => api.get("/categories/pinned/count"),
  getById: (id) => api.get(`/categories/${id}`),
  getBooks: (id) => api.get(`/categories/${id}/books`),
  create: (data) => api.post("/categories", data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  togglePin: (id) => api.put(`/categories/${id}/toggle-pin`),
};

export const readingProgressAPI = {
  get: (bookId) => {
    if (!bookId) return Promise.resolve({ data: null });
    return api.get(`/books/${bookId}/progress`);
  },
  update: (bookId, progress) => {
    if (!bookId) return Promise.resolve({ data: null });
    return api.post(`/books/${bookId}/progress`, progress);
  },
  getHistory: (bookId) => api.get(`/books/${bookId}/history`),
  getAllHistory: (params) => api.get("/books/user/history", { params }),
  addHistoryEntry: (bookId, actionType) =>
    api.post(`/books/${bookId}/history`, { action_type: actionType }),
};

export const annotationsAPI = {
  getForBook: (bookId) => api.get(`/books/${bookId}/annotations`),
  create: (bookId, data) => api.post(`/books/${bookId}/annotations`, data),
  update: (bookId, annotationId, data) =>
    api.put(`/books/${bookId}/annotations/${annotationId}`, data),
  delete: (bookId, annotationId) =>
    api.delete(`/books/${bookId}/annotations/${annotationId}`),
};

export const userAPI = {
  getSettings: () => api.get("/users/settings"),
  updateSettings: (settings) => api.put("/users/settings", settings),
  updateProfile: (data) => api.put("/users/profile", data),
  changePassword: (data) => api.put("/users/password", data),
};

export const applicationSettingsAPI = {
  getRegistrationStatus: () =>
    api.get("/application-settings/registration-status"),
  getAllSettings: () => api.get("/application-settings"),
  updateSetting: (key, data) => api.put(`/application-settings/${key}`, data),
  createSetting: (data) => api.post("/application-settings", data),
  deleteSetting: (key) => api.delete(`/application-settings/${key}`),
};

export const adminAPI = {
  // Dashboard
  getDashboardStats: async () => {
    try {
      const response = await api.get("/admin/dashboard/stats", {
        timeout: 5000,
        headers: {
          Accept: "application/json",
        },
      });
      return response;
    } catch (error) {
      console.error("Dashboard stats error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  },

  getRecentActivity: async () => {
    try {
      const response = await api.get("/admin/dashboard/activity", {
        timeout: 5000,
        headers: {
          Accept: "application/json",
        },
      });
      return response;
    } catch (error) {
      console.error("Activity API Error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  },

  // User management
  getUsers: async () => {
    try {
      return await api.get("/admin/users");
    } catch (error) {
      console.error("Get users error:", error);
      throw error;
    }
  },

  getUserDetails: async (userId) => {
    try {
      return await api.get(`/admin/users/${userId}/details`);
    } catch (error) {
      console.error("Get user details error:", error);
      // Minimal fallback to prevent UI from breaking
      return {
        data: {
          id: userId,
          username: `Unknown User`,
          email: ``,
          role: "",
          createdAt: "",
          lastLogin: "",
          totalStats: {
            booksCompleted: 0,
            totalPagesRead: 0,
            favouriteCategories: [],
            readingHistory: []
          }
        }
      };
    }
  },

  createUser: async (data) => {
    try {
      return await api.post("/admin/users", data);
    } catch (error) {
      console.error("Create user error:", error);
      throw error;
    }
  },

  updateUser: async (id, data) => {
    try {
      return await api.put(`/admin/users/${id}`, data);
    } catch (error) {
      console.error("Update user error:", error);
      throw error;
    }
  },

  deleteUser: async (id) => {
    try {
      return await api.delete(`/admin/users/${id}`);
    } catch (error) {
      console.error("Delete user error:", error);
      throw error;
    }
  },

  // Statistics
  getUserStats: async () => {
    try {
      const response = await api.get("/admin/stats/users");
      return response;
    } catch (error) {
      console.error("Get user stats error:", error);
      // Return minimal fallback data
      return {
        data: {
          totalUsers: 0,
          activeUsers: 0,
          newUsersThisWeek: 0,
          usersByRole: [],
          userActivity: {
            labels: [],
            datasets: []
          }
        }
      };
    }
  },

  getBookStats: async () => {
    try {
      const response = await api.get("/admin/stats/books");
      return response;
    } catch (error) {
      console.error("Get book stats error:", error);
      // Minimal fallback data to prevent UI breakage
      return {
        data: {
          totalBooks: 0,
          booksAddedThisMonth: 0,
          totalCategories: 0,
          topCategories: [],
          mostReadBooks: [],
          highestRatedBooks: []
        }
      };
    }
  },

  // Settings
  getSettings: async () => {
    try {
      return await api.get("/admin/settings");
    } catch (error) {
      console.error("Get settings error:", error);
      throw error;
    }
  },

  updateSettings: async (data) => {
    try {
      return await api.put("/admin/settings", data);
    } catch (error) {
      console.error("Update settings error:", error);
      throw error;
    }
  },
};

export const readingSessionAPI = {
  startSession: async (bookId) => {
    try {
      const response = await api.post(`/books/${bookId}/sessions/start`);
      return response.data;
    } catch (error) {
      console.error("Error starting reading session:", error);
      throw error;
    }
  },

  pauseSession: async (bookId, sessionId) => {
    try {
      const response = await api.post(
        `/books/${bookId}/sessions/${sessionId}/pause`
      );
      return response.data;
    } catch (error) {
      console.error("Error pausing reading session:", error);
      throw error;
    }
  },

  resumeSession: async (bookId, sessionId) => {
    try {
      const response = await api.post(
        `/books/${bookId}/sessions/${sessionId}/resume`
      );
      return response.data;
    } catch (error) {
      console.error("Error resuming reading session:", error);
      throw error;
    }
  },

  endSession: async (bookId, sessionId) => {
    try {
      const response = await api.post(
        `/books/${bookId}/sessions/${sessionId}/end`
      );
      return response.data;
    } catch (error) {
      console.error("Error ending reading session:", error);
      throw error;
    }
  },

  // Session information
  getCurrentSession: async (bookId) => {
    try {
      const response = await api.get(`/books/${bookId}/sessions/current`);
      return response.data;
    } catch (error) {
      console.error("Error getting current session:", error);
      throw error;
    }
  },

  getSessionStats: async (userId) => {
    try {
      // Attempt to get real data for all users (including user ID 6 now that backend is fixed)
      const response = await api.get(`/reading-sessions/stats/${userId}`);
      return {
        error: false,
        data: response.data
      };
    } catch (error) {
      console.error("Error getting session stats:", error);
      
      // Return an error flag with the error details
      return {
        error: true,
        errorCode: error.response?.status || 500,
        errorMessage: error.message,
        // Include an empty data structure to prevent UI from breaking
        data: {
          totalStats: {
            totalReadingTime: 0,
            averageSessionLength: 0,
            totalSessions: 0,
            activeSessions: 0
          },
          patterns: {
            hourly: Array(24).fill().map((_, i) => ({ hour: i, count: 0 })),
            daily: [
              { day: "Monday", count: 0 },
              { day: "Tuesday", count: 0 },
              { day: "Wednesday", count: 0 },
              { day: "Thursday", count: 0 },
              { day: "Friday", count: 0 },
              { day: "Saturday", count: 0 },
              { day: "Sunday", count: 0 }
            ]
          },
          recentSessions: []
        }
      };
    }
  },

  // Helper functions
  formatDuration: (minutes) => {
    if (!minutes) return "0m";
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${remainingMinutes}m`;
  },

  formatReadingPattern: (pattern) => {
    return {
      labels: pattern.map((p) => p.hour || p.dayOfWeek),
      data: pattern.map((p) => p.totalMinutes),
      sessions: pattern.map((p) => p.sessions),
    };
  },
};

export default api;


